/**
 * BattleForm (Foundry: rule-element/battle-form/rule-element.ts): a polymorph Effect that
 * replaces AC, skills, speeds, senses, size, traits, IWR and strikes with the form's own, keeping
 * the Character's status and circumstance modifiers on top (or the Character's own numbers when
 * `ownIfHigher` and they are higher). While the Effect is present the form is in force; removing
 * it restores everything, since the sheet is derived afresh each time.
 */
import { createModifier, resolveModifiers, signed, type Modifier } from "../modifiers";
import { localize, tableLabel } from "../labels";
import type { DerivedSpeed } from "../movement";
import { isValidPredicate, type RawPredicate } from "../predicate";
import { cleanRuleValue, resolveValue } from "../resolve";
import { sluggify } from "../slug";
import type { EngineState } from "../state";
import { recomputeCheck, recomputeDC, type DerivedStatistic } from "../statistics";
import { prepareStrike, prepareStrikes, type DerivedStrike } from "../strikes";
import { extractModifiers } from "../synthetics";
import { MOVEMENT_TYPES, SENSE_TYPES, SENSES_WITH_MANDATORY_ACUITIES, isSenseAcuity, isSkillSlug, type MovementType } from "../values";
import type { DamageDice } from "../damage-dice";
import type { EngineWeapon } from "../weapons";
import { RuleInstance, type RuleHandler } from "./base";
import { applyCreatureSize } from "./creature-size";
import { applyIWR, parseIWR } from "./iwr";
import { StrikeHandler, constructStrikeWeapon, type StrikeData } from "./strike";

interface BattleFormStrike {
  label?: string;
  img?: string;
  predicate?: RawPredicate;
  ability?: string;
  category: string;
  group: string | null;
  baseType?: string | null;
  traits: string[];
  modifier: unknown;
  damage: { damageType: string; dice: number; die: string; modifier?: unknown };
  ownIfHigher?: boolean;
  range?: { increment?: number | null; max?: number | null } | null;
  traitToggles?: { modular?: number | null; versatile?: string | null };
  handsHeld?: 1 | 2;
}

interface BattleFormOverrides {
  traits: string[];
  armorClass: { modifier: unknown; ignoreCheckPenalty: boolean; ignoreSpeedPenalty: boolean; ownIfHigher: boolean };
  tempHP: unknown;
  senses: Record<string, { acuity?: string; range?: number | null }>;
  size: string | null;
  speeds: Partial<Record<MovementType, unknown>>;
  skills: Record<string, { modifier: unknown; ownIfHigher?: boolean }>;
  strikes: Record<string, BattleFormStrike>;
  immunities: Record<string, unknown>[];
  weaknesses: Record<string, unknown>[];
  resistances: Record<string, unknown>[];
}

interface BattleFormData {
  overrides: BattleFormOverrides;
  brackets: { start: number; value: Record<string, unknown> }[];
  ownUnarmed: boolean;
  canCast: boolean;
  canSpeak: boolean;
  hasHands: boolean;
  modifierLabel: string;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function deepMerge(target: Record<string, unknown>, source: Record<string, unknown>): Record<string, unknown> {
  for (const [key, value] of Object.entries(source)) {
    const current = target[key];
    if (isPlainObject(value) && isPlainObject(current)) deepMerge(current, value);
    else target[key] = isPlainObject(value) ? structuredClone(value) : value;
  }
  return target;
}

function parseOverrides(raw: unknown): BattleFormOverrides {
  const o = isPlainObject(raw) ? raw : {};
  const ac = isPlainObject(o.armorClass) ? o.armorClass : {};
  return {
    traits: Array.isArray(o.traits) ? o.traits.filter((t): t is string => typeof t === "string") : [],
    armorClass: { modifier: ac.modifier === undefined ? 0 : cleanRuleValue(ac.modifier), ignoreCheckPenalty: ac.ignoreCheckPenalty !== false, ignoreSpeedPenalty: ac.ignoreSpeedPenalty !== false && ac.ignoreSpeedReduction !== false, ownIfHigher: ac.ownIfHigher === true },
    tempHP: o.tempHP === undefined || o.tempHP === null ? null : cleanRuleValue(o.tempHP),
    senses: isPlainObject(o.senses) ? (o.senses as BattleFormOverrides["senses"]) : {},
    size: typeof o.size === "string" && o.size ? o.size : null,
    speeds: isPlainObject(o.speeds) ? (o.speeds as BattleFormOverrides["speeds"]) : {},
    skills: isPlainObject(o.skills) ? (o.skills as BattleFormOverrides["skills"]) : {},
    strikes: isPlainObject(o.strikes) ? (o.strikes as BattleFormOverrides["strikes"]) : {},
    immunities: Array.isArray(o.immunities) ? o.immunities.filter(isPlainObject) : [],
    weaknesses: Array.isArray(o.weaknesses) ? o.weaknesses.filter(isPlainObject) : [],
    resistances: Array.isArray(o.resistances) ? o.resistances.filter(isPlainObject) : [],
  };
}

function parse(rule: RuleInstance): void {
  const s = rule.source;
  const overridesRaw = isPlainObject(s.overrides) ? structuredClone(s.overrides) : {};
  const brackets = (Array.isArray(s.brackets) ? s.brackets : []).filter(isPlainObject).map((b) => ({ start: typeof b.start === "number" ? b.start : 0, value: isPlainObject(b.value) ? b.value : {} }));
  const level = rule.item.level ?? 0;
  const bracket = [...brackets].reverse().find((b) => b.start <= level);
  if (bracket) deepMerge(overridesRaw, bracket.value);
  for (const strike of Object.values(isPlainObject(overridesRaw.strikes) ? overridesRaw.strikes : {})) {
    if (isPlainObject(strike) && typeof strike.query === "string") return rule.fail("strikes: a compendium query strike cannot be resolved here");
  }
  rule.data = { overrides: parseOverrides(overridesRaw), brackets, ownUnarmed: s.ownUnarmed === true, canCast: s.canCast === true, canSpeak: s.canSpeak === true, hasHands: s.hasHands === true, modifierLabel: rule.reducedLabel } satisfies BattleFormData as unknown as Record<string, unknown>;
}

/** Modifiers the form keeps: its own, and status or circumstance ones (or penalties); never ability modifiers. */
function filterModifier(modifier: Modifier): boolean {
  if (modifier.slug === "battle-form") return true;
  if (modifier.type === "ability") return false;
  return ["status", "circumstance"].includes(modifier.type) || modifier.value < 0;
}

function useFormModifier(args: { form: number; own: number; modifiers: readonly Modifier[]; ownIfHigher: boolean }): boolean {
  const shared = args.modifiers.filter((m) => m.enabled && filterModifier(m)).reduce((sum, m) => sum + m.value, 0);
  return !args.ownIfHigher || args.form + shared >= args.own;
}

function suppressModifiers(modifiers: Modifier[], label: string): void {
  for (const modifier of modifiers) {
    if (!filterModifier(modifier)) {
      modifier.ignored = true;
      modifier.enabled = false;
      modifier.adjustedBy.push(label);
    }
  }
}

function beforePrepareData(rule: RuleInstance, state: EngineState): void {
  if (rule.ignored) return;
  const flags = state.actor.flags.pf2e;
  if (flags.polymorphed) {
    rule.ignored = true;
    return;
  }
  flags.polymorphed = true;
  flags.battleForm = true;
  const data = rule.data as unknown as BattleFormData;
  state.battleForm = { item: rule.item, rule, ownUnarmed: data.ownUnarmed };

  // Roll options and hands free.
  state.rollOptions.set("all", "polymorph");
  state.rollOptions.set("all", "battle-form");
  for (const key of Object.keys(data.overrides.skills)) if (isSkillSlug(key)) state.rollOptions.set("all", `battle-form:${key}`);
  const handsFree = Math.max(Object.values(data.overrides.strikes).reduce((count, s) => (s.category === "unarmed" ? count : count - 1), 2), 0);
  state.actor.system.attributes.handsFree = handsFree;
  for (const n of [0, 1, 2]) {
    if (handsFree === n) state.rollOptions.set("all", `hands-free:${n}`);
    else state.rollOptions.delete("all", `hands-free:${n}`);
  }

  // Senses: forced, as Sense Rule Elements.
  for (const [key, sense] of Object.entries(data.overrides.senses)) {
    const type = sluggify(key);
    if (!(SENSE_TYPES as readonly string[]).includes(type)) return rule.fail(`senses: ${type} is not a valid choice`);
    const mandatory = SENSES_WITH_MANDATORY_ACUITIES[type as keyof typeof SENSES_WITH_MANDATORY_ACUITIES];
    const acuity = mandatory ?? (isSenseAcuity(sense.acuity) ? sense.acuity : "precise");
    const range = typeof sense.range === "number" ? sense.range : Infinity;
    state.synthetics.senses.push({ sense: { type, acuity, range, source: rule.item.name }, predicate: [], force: true });
  }

  for (const trait of data.overrides.traits) if (!state.actor.system.traits.value.includes(trait)) state.actor.system.traits.value.push(trait);
  const suppression = { test: () => true, suppress: true, source: rule.item.uuid, sourceName: rule.item.name };
  if (data.overrides.armorClass.ignoreCheckPenalty) (state.synthetics.modifierAdjustments["skill-check"] ??= []).push({ slug: "armor-check-penalty", ...suppression });
  if (data.overrides.armorClass.ignoreSpeedPenalty) (state.synthetics.modifierAdjustments["all-speeds"] ??= []).push({ slug: "armor-speed-penalty", ...suppression });
}

function afterPrepareData(rule: RuleInstance, state: EngineState): void {
  if (rule.ignored || !state.derived) return;
  const data = rule.data as unknown as BattleFormData;
  prepareAC(rule, state, data);
  prepareSize(rule, state, data);
  prepareSkills(rule, state, data);
  prepareSpeeds(rule, state, data);
  prepareStrikesForForm(rule, state, data);
  prepareIWR(rule, state, data);
  const tempHP = data.overrides.tempHP === null ? null : Math.trunc(Number(resolveValue(rule, state, data.overrides.tempHP)));
  if (typeof tempHP === "number" && tempHP > 0) state.synthetics.tempHP.push({ value: tempHP, events: { onCreate: true, onTurnStart: false }, predicate: [], enabled: true, source: rule.item.uuid, sourceName: rule.item.name });
}

function prepareAC(rule: RuleInstance, state: EngineState, data: BattleFormData): void {
  const ac = state.derived!.ac;
  const acOverride = Number(resolveValue(rule, state, data.overrides.armorClass.modifier, ac.dc.value)) || 0;
  if (!acOverride) return;
  if (!useFormModifier({ form: acOverride, own: ac.dc.value, modifiers: ac.dc.modifiers, ownIfHigher: data.overrides.armorClass.ownIfHigher })) return;
  suppressModifiers(ac.dc.modifiers, data.modifierLabel);
  suppressModifiers(ac.check.modifiers, data.modifierLabel);
  const formModifier = createModifier({ slug: "battle-form", label: data.modifierLabel, value: acOverride - 10, type: "untyped", source: rule.item.uuid, sourceName: rule.item.name });
  ac.dc.modifiers.push(formModifier);
  ac.check.modifiers.push(createModifier({ slug: "battle-form", label: data.modifierLabel, value: acOverride - 10, type: "untyped", source: rule.item.uuid, sourceName: rule.item.name }));
  recomputeDC(ac.dc);
  recomputeCheck(ac.check);
  state.actor.system.attributes.ac.value = ac.dc.value;
}

function prepareSize(rule: RuleInstance, state: EngineState, data: BattleFormData): void {
  if (!data.overrides.size) return;
  applyCreatureSize(rule, state, { value: data.overrides.size, reach: null, resizeEquipment: false, minimumSize: null, maximumSize: null });
}

function prepareSkills(rule: RuleInstance, state: EngineState, data: BattleFormData): void {
  const skills = state.derived!.skills;
  for (const [key, newSkill] of Object.entries(data.overrides.skills)) {
    const current = skills[key];
    if (!current) return rule.fail(`Unrecognized skill: ${key}`);
    const ownIfHigher = newSkill.ownIfHigher ?? true;
    const newModifier = Number(resolveValue(rule, state, newSkill.modifier)) || 0;
    if (!useFormModifier({ form: newModifier, own: current.check.mod, modifiers: current.check.modifiers, ownIfHigher })) continue;
    const baseModifier = () => createModifier({ label: data.modifierLabel, slug: "battle-form", value: newModifier, type: "untyped", source: rule.item.uuid, sourceName: rule.item.name });
    suppressModifiers(current.check.modifiers, data.modifierLabel);
    current.check.modifiers.unshift(baseModifier());
    recomputeCheck(current.check);
    suppressModifiers(current.dc.modifiers, data.modifierLabel);
    current.dc.modifiers.unshift(baseModifier());
    recomputeDC(current.dc);
    const trace = state.actor.system.skills[key];
    if (trace) trace.value = current.check.mod;
  }
}

function prepareSpeeds(rule: RuleInstance, state: EngineState, data: BattleFormData): void {
  const speeds = state.derived!.speeds;
  for (const type of MOVEMENT_TYPES) {
    const override = resolveValue(rule, state, data.overrides.speeds[type], null, { warn: false });
    if (typeof override !== "number") continue;
    const domains = ["all-speeds", "speed", `${type}-speed`];
    const options = state.rollOptions.get(domains);
    const modifiers = extractModifiers(state.synthetics, domains, { test: options });
    const resolved = resolveModifiers(modifiers, options);
    suppressModifiers(resolved.modifiers, data.modifierLabel);
    const total = resolved.modifiers.filter((m) => m.enabled).reduce((sum, m) => sum + m.value, 0);
    const base = Math.max(0, Math.trunc(override));
    const value = base > 0 ? Math.max(5, base + total) : Math.max(0, base + total);
    const typeName = tableLabel(state, "movementTypes", type);
    const label = localize(state, "PF2E.Actor.Speed.Type.Label", "{type} Speed", { type: typeName });
    const breakdown = [localize(state, "PF2E.Actor.Speed.BaseWithSource", "{value}-Foot {type} Speed ({source})", { value: base, type: typeName, source: rule.item.name }), ...resolved.modifiers.filter((m) => m.enabled && m.value !== 0).map((m) => `${m.label} ${signed(m.value)}`)].join(", ");
    const speed: DerivedSpeed = { type, label, base, source: rule.item.name, displayBase: base, value, modifiers: resolved.modifiers, breakdown, derivedFrom: null, domains };
    speeds[type] = speed;
    const trace = { type, value, base, source: rule.item.name, label, breakdown };
    if (type === "land") state.actor.system.movement.speeds.land = trace;
    else state.actor.system.movement.speeds[type] = trace;
    if (type === "land") state.actor.system.attributes.speed.value = value;
  }
}

function prepareStrikesForForm(rule: RuleInstance, state: EngineState, data: BattleFormData): void {
  const synthetics = state.synthetics;
  const strikes = data.overrides.strikes;
  const ruleData: Record<string, unknown>[] = Object.entries(strikes).map(([slug, strike]) => ({
    key: "Strike",
    label: strike.label ? (state.catalogue?.localize(strike.label) ?? strike.label) : localize(state, `PF2E.BattleForm.Attack.${slug.split("-").map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join("")}`, slug.charAt(0).toUpperCase() + slug.slice(1)),
    slug,
    predicate: isValidPredicate(strike.predicate) ? strike.predicate : [],
    img: strike.img ?? "",
    category: strike.category,
    group: strike.group,
    baseType: strike.baseType ?? null,
    options: [slug],
    damage: { base: { ...strike.damage, modifier: Number(resolveValue(rule, state, strike.damage.modifier ?? 0)) || 0 } },
    range: strike.range ?? null,
    traits: [...(strike.traits ?? [])],
    ability: strike.ability ?? null,
    traitToggles: strike.traitToggles ?? { modular: null, versatile: null },
    handsHeld: strike.handsHeld ?? 1,
    battleForm: true,
  }));

  if (data.ownUnarmed) {
    for (const [slug, deferred] of Object.entries(synthetics.strikes)) {
      const owner = state.rules.find((r) => r.key === "Strike" && !r.ignored && (r.data as unknown as StrikeData).slug === slug);
      if (owner && (owner.data as unknown as StrikeData).category !== "unarmed") {
        owner.ignored = true;
        delete synthetics.strikes[slug];
      }
      void deferred;
    }
    state.rollOptions.set("all", "battle-form:own-attack-modifier");
  } else {
    for (const r of state.rules) {
      if (r.ignored) continue;
      if (r.key === "Strike" && !(r.data as unknown as StrikeData).battleForm) {
        r.ignored = true;
        delete synthetics.strikes[(r.data as unknown as StrikeData).slug];
      }
    }
    for (const striking of Object.values(synthetics.striking).flat()) striking.predicate.push({ not: "battle-form" });
    for (const datum of ruleData) {
      const traits = datum.traits as string[];
      if (!traits.includes("magical")) traits.push("magical");
      const instance = new RuleInstance(rule.item, rule.index, { ...datum, key: "Strike" }, StrikeHandler);
      StrikeHandler.parse(instance, state);
      if (instance.ignored) {
        rule.problems.push(...instance.problems);
        continue;
      }
      if (!instance.test(state)) continue;
      const strikeData = instance.data as unknown as StrikeData;
      synthetics.strikes[strikeData.slug] = (unarmedRunes) => constructStrikeWeapon(instance, state, unarmedRunes);
    }
    // Notes on damage selectors are the Character's own: a battle form does not use them.
    for (const [key, notes] of Object.entries(synthetics.rollNotes)) {
      if (!/\bdamage\b/.test(key)) continue;
      for (const note of notes) if (!note.predicate.includes("battle-form")) note.predicate.push({ not: "battle-form" });
    }
  }

  const prepared = prepareStrikes(state, { includeBasicUnarmed: data.ownUnarmed }).filter((s) => s.weapon.battleForm || (data.ownUnarmed && s.weapon.category === "unarmed"));
  const apply = (action: DerivedStrike): void => {
    const strike = strikes[action.slug];
    if (!strike) return;
    const form = Number(resolveValue(rule, state, strike.modifier)) || 0;
    const useForm = useFormModifier({ form, own: action.totalModifier, modifiers: action.modifiers, ownIfHigher: strike.ownIfHigher ?? true });
    if (!data.ownUnarmed && useForm) {
      suppressModifiers(action.modifiers, data.modifierLabel);
      action.modifiers.unshift(createModifier({ slug: "battle-form", label: data.modifierLabel, value: form, type: "untyped", source: rule.item.uuid, sourceName: rule.item.name }));
      const total = action.modifiers.filter((m) => m.enabled).reduce((sum, m) => sum + m.value, 0);
      action.totalModifier = total;
      action.breakdown = action.modifiers.filter((m) => m.enabled).map((m) => `${m.label} ${signed(m.value)}`).join(", ");
      for (const variant of action.variants) {
        variant.total = total + variant.penalty;
        variant.label = variant.penalty ? localize(state, "PF2E.MAPAbbreviationValueLabel", "{value} (MAP {penalty})", { value: signed(variant.total), penalty: variant.penalty }) : signed(total);
      }
    } else {
      state.rollOptions.set("strike-attack-roll", "battle-form:own-attack-modifier");
      const again = prepareStrike(state, action.weapon, state.handsReallyFree);
      Object.assign(action, { modifiers: again.modifiers, totalModifier: again.totalModifier, breakdown: again.breakdown, variants: again.variants });
    }
  };
  for (const action of prepared) {
    apply(action);
    for (const alt of action.altUsages) apply(alt);
  }
  state.strikes = prepared;
}

function prepareIWR(rule: RuleInstance, state: EngineState, data: BattleFormData): void {
  const run = (kind: "immunity" | "weakness" | "resistance", key: string, sources: Record<string, unknown>[], override: boolean): void => {
    for (const source of sources) {
      const instance = new RuleInstance(rule.item, rule.index, { ...source, key }, null);
      const parsed = parseIWR(instance, kind);
      if (!parsed) {
        rule.problems.push(...instance.problems);
        continue;
      }
      applyIWR(instance, state, parsed, { override: override || parsed.override, sourceName: rule.item.name });
      rule.problems.push(...instance.problems);
    }
  };
  run("immunity", "Immunity", data.overrides.immunities, false);
  run("weakness", "Weakness", data.overrides.weaknesses, true);
  run("resistance", "Resistance", data.overrides.resistances, true);
}

/** Disable damage parts a battle form's strikes may not use (Foundry: applyDamageExclusion). */
function applyDamageExclusion(rule: RuleInstance, _state: EngineState, weapon: EngineWeapon, parts: (Modifier | DamageDice)[]): void {
  const data = rule.data as unknown as BattleFormData;
  if (data.ownUnarmed || rule.ignored) return;
  for (const part of parts) {
    if (part.predicate.some((s) => isPlainObject(s) && "not" in s && s.not === "battle-form")) continue;
    const isModifier = "value" in part && !("diceNumber" in part);
    const isNumericBonus = isModifier && (part as Modifier).value >= 0;
    const isAbilityModifier = isModifier && (part as Modifier).type === "ability";
    const isExtraDice = !isModifier;
    const isStatusOrCircumstance = isNumericBonus && ["status", "circumstance"].includes((part as Modifier).type);
    const isDamageTrait = isExtraDice && /^(?:deadly|fatal)-\d?d\d{1,2}$/.test(part.slug) && (data.overrides.strikes[weapon.slug]?.traits ?? []).includes(part.slug);
    const isBattleFormModifier = part.predicate.includes("battle-form") || part.predicate.some((s) => isPlainObject(s) && "or" in s && Array.isArray(s.or) && s.or.includes("battle-form")) || isDamageTrait;
    if ((isNumericBonus || isAbilityModifier || isExtraDice) && !isStatusOrCircumstance && !isBattleFormModifier) {
      part.enabled = false;
      part.ignored = true;
      part.predicate.push({ not: "battle-form" });
    }
  }
}

export const BattleFormHandler: RuleHandler = { key: "BattleForm", parse, beforePrepareData, afterPrepareData, applyDamageExclusion };
export type { DerivedStatistic };
